import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { useCheckDispenseStatusMutation } from '../../../services/prescriptions'
import { FetchBaseQueryErrorResult } from '../../../services/baseQuery'
import { DispenseStatusItem, GeneralError, Prescription } from '../../../types'
import PrescribedItemStateTag from './PrescribedItemStateTag'
import { PrescribedItemState } from '../../../types/prescription'

interface DispenseStatusModalProps {
  isOpen: boolean
  onClose: () => void
  prescription?: Prescription
  canCease: boolean
  onCease: () => void
}

// eRx dates are Australian calendar dates ("2026-09-29" or "2026-09-29T08:31:35.333", no offset).
// Read the date part as written so the workstation's timezone can't move it to another day.
export const formatErxDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) {
    return value
  }
  const [, year, month, day] = match
  return format(new Date(Number(year), Number(month) - 1, Number(day)), 'd MMM yyyy')
}

// eRx sends Australian numbers as bare digits, e.g. 0355504123 or 0412345678.
export const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, '')
  if (/^04\d{8}$/.test(digits)) {
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
  }
  if (/^0[2378]\d{8}$/.test(digits)) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)} ${digits.slice(6)}`
  }
  return value
}

const describeSupply = (supplyNumber: number | null, repeats: number | null) => {
  const total = repeats === null ? null : repeats + 1
  if (supplyNumber === 1) {
    return total ? `Original (1 of ${total})` : 'Original'
  }
  if (supplyNumber === null) {
    return null
  }
  return total ? `Supply ${supplyNumber} of ${total}` : `Supply ${supplyNumber}`
}

const errorMessage = (error?: GeneralError) => {
  switch (error?.code) {
    case 'erx_rate_limited':
      return 'eRx is busy. Check again in a minute.'
    case 'dispense_status_check_in_progress':
      return 'Another check for this script is still running. Check again in a moment.'
    case 'dispense_status_changed':
      return 'This script changed while it was being checked. Check again.'
    default:
      return "Couldn't get the dispense status from eRx. Check again in a few minutes. If it keeps failing, contact support."
  }
}

const Fact: FunctionComponent<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <>
    <Text color="gray.600">{label}</Text>
    <Box minW={0}>{children}</Box>
  </>
)

const CopyablePhone: FunctionComponent<{ phoneNumber: string }> = ({ phoneNumber }) => {
  const [copied, setCopied] = useState(false)
  const formatted = formatPhoneNumber(phoneNumber)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formatted)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }
  return (
    <Flex align="center" gap={2}>
      <Text>{formatted}</Text>
      <Button size="xs" variant="link" colorScheme="blue" onClick={copy}>
        {copied ? 'Copied' : 'Copy'}
      </Button>
    </Flex>
  )
}

const DispenseDetails: FunctionComponent<{ item: DispenseStatusItem }> = ({ item }) => {
  const { dispensedSupply, pharmacy, dispensedPatient } = item
  const supply =
    dispensedSupply && describeSupply(dispensedSupply.supplyNumber, item.numberOfRepeatsAuthorised)
  const pharmacyLocation = pharmacy
    ? [
        [pharmacy.address1, pharmacy.address2].filter(Boolean).join(', '),
        [pharmacy.suburb, pharmacy.state, pharmacy.postcode].filter(Boolean).join(' '),
      ]
        .filter(Boolean)
        .join(', ')
    : ''
  const recordedPatient = dispensedPatient
    ? [dispensedPatient.firstName, dispensedPatient.familyName].filter(Boolean).join(' ')
    : ''

  return (
    <Grid templateColumns={['1fr', 'max-content 1fr']} columnGap={6} rowGap={[1, 2]}>
      {supply && <Fact label="Supply">{supply}</Fact>}
      {dispensedSupply?.quantity !== null && dispensedSupply?.quantity !== undefined && (
        <Fact label="Quantity">{dispensedSupply.quantity}</Fact>
      )}
      {pharmacy && (
        <Fact label="Pharmacy">
          <Text>{pharmacy.name}</Text>
          {pharmacyLocation && (
            <Text fontSize="sm" color="gray.500">
              {pharmacyLocation}
            </Text>
          )}
        </Fact>
      )}
      {pharmacy?.phoneNumber && (
        <Fact label="Phone">
          <CopyablePhone phoneNumber={pharmacy.phoneNumber} />
        </Fact>
      )}
      {recordedPatient && (
        <Fact label="Recorded patient">
          <Text>{recordedPatient}</Text>
          <Text fontSize="sm" color="gray.500">
            Name the pharmacy entered when dispensing
          </Text>
        </Fact>
      )}
    </Grid>
  )
}

const ItemStatus: FunctionComponent<{
  item: DispenseStatusItem
  isOneOfSeveral: boolean
}> = ({ item, isOneOfSeveral }) => {
  const { state, dispensedSupply, updatedDate } = item

  let headline = `eRx status: ${state}`
  if (state === 'Dispensed' && dispensedSupply) {
    headline = `Original supply dispensed on ${formatErxDate(dispensedSupply.dispensedOn)}`
  } else if (state === 'Active') {
    headline = 'Not dispensed yet'
  } else if ((state === 'Ceased' || state === 'Cancelled') && updatedDate) {
    headline = `${state} on ${formatErxDate(updatedDate)}`
  }

  return (
    <VStack align="stretch" spacing={4}>
      <Flex align="center" gap={3} wrap="wrap">
        <PrescribedItemStateTag state={state as PrescribedItemState} />
        <Text fontWeight="semibold" fontSize="md">
          {headline}
        </Text>
      </Flex>

      {state === 'Active' && (
        <Text color="gray.600">
          No pharmacy has dispensed this {isOneOfSeveral ? 'item' : 'script'} yet.
        </Text>
      )}
      {state === 'Ceased' && <Text color="gray.600">Remaining repeats can't be dispensed.</Text>}

      {dispensedSupply && <DispenseDetails item={item} />}

      {dispensedSupply && (
        <Alert status="info" borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <AlertDescription fontSize="sm">
            Repeats aren't shown here. The pharmacy issues each repeat as a new script, so its
            dispensing record stays with them. To find out where a repeat was dispensed, contact the
            pharmacy.
          </AlertDescription>
        </Alert>
      )}
    </VStack>
  )
}

export const DispenseStatusModal: FunctionComponent<DispenseStatusModalProps> = ({
  isOpen,
  onClose,
  prescription,
  canCease,
  onCease,
}) => {
  const [checkDispenseStatus, result] = useCheckDispenseStatusMutation()
  const scid = prescription?.scid

  useEffect(() => {
    if (isOpen && scid) {
      checkDispenseStatus(scid)
    }
  }, [isOpen, scid, checkDispenseStatus])

  const onCloseModal = () => {
    result.reset()
    onClose()
  }

  // Reset here too, so reopening for another script never shows this script's result.
  const ceaseScript = () => {
    result.reset()
    onCease()
  }

  const isChecking = result.isLoading
  const error = result.isError
    ? ((result.error as FetchBaseQueryErrorResult['error'])?.data as GeneralError)
    : undefined
  const status =
    !isChecking && !result.isError && result.data?.scid === scid ? result.data : undefined
  const createdDate = prescription ? format(new Date(prescription.createdDate), 'd MMM yyyy') : ''
  // Repeats are authorised per item, so the summary only states them for a single-item script.
  const singleItem =
    prescription?.prescribedItems.length === 1 ? prescription.prescribedItems[0] : undefined
  const repeats = singleItem?.numberOfRepeatsAuthorised
  // Cease acts on the whole script, so offer it once if any dispensed item has repeats.
  const canStopRepeats =
    canCease &&
    !!status?.items.some(
      (item) => item.state === 'Dispensed' && (item.numberOfRepeatsAuthorised ?? 0) > 0
    )

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} blockScrollOnMount={false}>
      <ModalOverlay />
      <ModalContent width="100%" maxWidth="600px">
        <ModalHeader>Dispense status</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={5}>
            {prescription && (
              <Box pb={4} borderBottomWidth="1px" borderColor="gray.200">
                <Text fontWeight="semibold">
                  {prescription.prescribedItems.map((item) => item.displayName).join(', ')}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  SCID {prescription.scid}, created {createdDate}
                  {repeats !== undefined &&
                    `, ${repeats} ${Number(repeats) === 1 ? 'repeat' : 'repeats'}`}
                </Text>
              </Box>
            )}

            {isChecking && (
              <Flex direction="column" align="center" gap={3} py={6} color="gray.600" role="status">
                <Spinner color="blue.500" />
                <Text>Checking with eRx</Text>
              </Flex>
            )}

            {error !== undefined && (
              <Alert status="error" borderRadius="md" role="alert">
                <AlertIcon />
                <AlertDescription fontSize="sm">{errorMessage(error)}</AlertDescription>
              </Alert>
            )}

            {status?.items.map((item) => (
              <Box key={item.sequence}>
                {status.items.length > 1 && (
                  <Text fontWeight="semibold" mb={2}>
                    {prescription?.prescribedItems.find((p) => p.sequence === item.sequence)
                      ?.displayName ?? `Item ${item.sequence}`}
                  </Text>
                )}
                <ItemStatus item={item} isOneOfSeveral={status.items.length > 1} />
              </Box>
            ))}

            {canStopRepeats && (
              <Alert status="warning" borderRadius="md" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <AlertDescription fontSize="sm" display="block">
                    This script can no longer be cancelled. To stop any remaining repeats, cease the
                    script.
                  </AlertDescription>
                  <Button
                    mt={3}
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={ceaseScript}
                  >
                    Cease script
                  </Button>
                </Box>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter justifyContent="space-between" gap={3} flexWrap="wrap">
          <Text fontSize="sm" color="gray.500">
            {status ? `Checked with eRx at ${format(new Date(status.checkedAt), 'h:mm a')}` : ''}
          </Text>
          <Flex gap={3}>
            <Button onClick={onCloseModal}>Close</Button>
            <Button
              colorScheme="blue"
              isDisabled={isChecking || !scid}
              onClick={() => scid && checkDispenseStatus(scid)}
            >
              Check again
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
